import React, { useCallback } from "react";
import ReactDOM from "react-dom";
import Card from "../../../molecules/Card";
import PropTypes from "prop-types";

import "./index.scss";
import Button from "../../../atoms/Button";
import {TextArea} from "../../../atoms/TextArea";
import PopupOverlay from "../../Background/PopupOverlay";
import ImageUploadButton from "../../../molecules/ImageUploadButton";
import PostVisibilityControl from "../../../molecules/PostVisibilityControl";
import EmojiPicker from "../../../molecules/EmojiPicker";
import PostAttachmentsPicker from "../../../molecules/PostAttachmentsPicker";
import { MAX_POST_ATTACHMENTS } from "../../../../constants/posts";
import { extractImageFilesFromClipboardEvent } from "../../../../utils/clipboard";

export function NewPostModal({
    mode,
    postContent,
    setPostContent,
    postVisibility,
    setPostVisibility,
    attachments,
    onAddAttachments,
    onRemoveAttachment,
    attachmentError,
    maxAttachments,
    visibilityOptions,
    isSubmitting,
    onSubmit,
    onClose,
}) {

    const isEdit = mode === 'edit';
    const postTitle = isEdit ? "Update post" : "Create new post";
    const hasContent = (postContent && postContent.trim().length > 0) || (attachments && attachments.length > 0);
    const totalAttachments = Array.isArray(attachments) ? attachments.length : 0;
    const canAddAttachments = totalAttachments < maxAttachments;

    // This function is called by EmojiPicker on each emoji click
    const handleSelectEmoji = (emoji) => {
        setPostContent((prev) => prev + emoji.native);
    };

    const handlePaste = useCallback((event) => {
        const files = extractImageFilesFromClipboardEvent(event);
        if (!files.length) {
            return;
        }
        event.preventDefault();
        onAddAttachments?.(files);

        const pastedText = event.clipboardData?.getData("text/plain");
        if (pastedText && pastedText.trim()) {
            setPostContent((prev) => `${prev}${prev && !prev.endsWith('\n') ? '\n' : ''}${pastedText}`);
        }
    }, [onAddAttachments, setPostContent]);

    return ReactDOM.createPortal(
        <>
            <PopupOverlay>
                <Card
                    id="new-post-card"
                    allowOverflow
                    title={<strong className="new-post-modal__title">{postTitle}</strong>}
                    body={
                        <div className="new-post-modal__body">
                            <TextArea
                                value={postContent}
                                onChange={(e) => setPostContent(e.target.value)}
                                placeholder="What's on your mind?"
                                onPaste={handlePaste}
                                disabled={isSubmitting}
                            />
                            <PostAttachmentsPicker
                                attachments={attachments}
                                onRemoveAttachment={onRemoveAttachment}
                                maxAttachments={maxAttachments}
                                error={attachmentError}
                                disabled={isSubmitting}
                            />
                        </div>
                    }
                    footer={
                        <div className="new-post-modal__footer">
                            <div className="new-post-modal__footer-tools">
                                <ImageUploadButton
                                    onSelectImages={onAddAttachments}
                                    buttonClassName="emoji-btn new-post-modal__image-button"
                                    disabled={isSubmitting || !canAddAttachments}
                                />
                                <EmojiPicker onSelectEmoji={handleSelectEmoji} />
                            </div>

                            <div className="new-post-modal__visibility">
                                <PostVisibilityControl
                                    value={postVisibility}
                                    onChange={setPostVisibility}
                                    disabled={isSubmitting}
                                    size="md"
                                    className="post-card__visibility-control new-post-modal__visibility-control"
                                    options={visibilityOptions}
                                />
                            </div>

                            <div className="new-post-modal__actions">
                                <Button
                                    variant="secondary"
                                    shape="pill"
                                    className="new-post-modal__action"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    variant="primary"
                                    shape="pill"
                                    className="new-post-modal__action"
                                    onClick={onSubmit}
                                    disabled={!hasContent || isSubmitting}
                                >
                                    {isSubmitting ? 'Saving…' : (isEdit ? "Update" : "Post")}
                                </Button>
                            </div>
                        </div>
                    }
                />
            </PopupOverlay>
        </>,
        document.getElementById("portal")
    );
}

NewPostModal.propTypes = {
    mode: PropTypes.oneOf(['create', 'edit']).isRequired,
    postContent: PropTypes.string.isRequired,
    setPostContent: PropTypes.func.isRequired,
    postVisibility: PropTypes.string.isRequired,
    setPostVisibility: PropTypes.func.isRequired,
    attachments: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        previewUrl: PropTypes.string,
        source: PropTypes.oneOf(['local', 'remote']).isRequired,
    })).isRequired,
    onAddAttachments: PropTypes.func.isRequired,
    onRemoveAttachment: PropTypes.func.isRequired,
    attachmentError: PropTypes.string,
    maxAttachments: PropTypes.number,
    visibilityOptions: PropTypes.array,
    isSubmitting: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

NewPostModal.defaultProps = {
    attachmentError: null,
    maxAttachments: MAX_POST_ATTACHMENTS,
    visibilityOptions: undefined,
    isSubmitting: false,
};
